import {useLayoutEffect, useEffect, useRef} from "react";
import {
    Route,
    Routes,
    useLocation,
    useNavigate,
    useSearchParams,
} from "react-router-dom";
import {observer} from "mobx-react-lite";
import {PrimeReactProvider} from "primereact/api";
import {Toast} from "primereact/toast";

import appStore from "./storage/AppStore";
import clubStore from "./storage/ClubStore";
import {useTelegram} from "./hooks/useTelegram";
import { applyBranding } from './common/applyBranding';

// Pages
import Client from "./components/Client/Client";
import Companies from "./components/Companies/Companies";
import AdminCompanies from "./components/Admin/Companies";
import Settings from "./components/Settings/Settings";
import AdminHome from "./components/Admin/Home";
import NewCompany from "./components/Admin/NewCompany";
import EditCompany from "./components/Admin/EditCompany";
import ManagerEditCompany from './components/Clubs/Manager/EditCompany';
import Members from "./components/Clubs/Manager/Members";
import Branches from "./components/Clubs/Manager/Branches";
import NewBranch from "./components/Clubs/Manager/NewBranch";
import EditBranch from "./components/Clubs/Manager/EditBranch";
import NewTeam from "./components/Clubs/Manager/NewTeam";
import ManagerTeams from "./components/Clubs/Manager/Teams";
import ShowTeam from "./components/Clubs/Manager/ShowTeam";
import EditTeam from "./components/Clubs/Manager/EditTeam";
import TrainerTeams from "./components/Clubs/Trainer/Teams";
import Trainings from "./components/Clubs/Trainer/Trainings";
import NotFound from "./components/NotFound/NotFound";
import Attendance from "./components/Clubs/Parent/Attendance";


// Styles
// Styles — order matters

// 1. Third-party base styles
import "primereact/resources/themes/lara-light-cyan/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "primeflex/primeflex.css";

// 2. Our design system / global overrides
import "./common/style.css";

// 3. Global layout
import "./common/page.css";

// 4. PrimeReact component overrides
import "./common/calendar.css";
import "./common/dialog.css";
import "./common/multiselect.css";
import "./common/autocomplete.css";

import {Badge} from "primereact/badge";
import Loading from './components/Loading/Loading'
import Notification from "./components/Notification/Notification";
import AttendanceManager from './components/Clubs/Manager/AttendanceManager'
import ShowCompany from './components/Clubs/Manager/ShowCompany'
import BillingInfo from "./components/Clubs/Manager/BillingInfo";
import BillingDetail from "./components/Clubs/Manager/BillingDetail";
import BillingGroupDetail from "./components/Clubs/Manager/BillingGroupDetail";
import AttendanceDetail from './components/Clubs/Manager/AttendanceDetail'
import NewBilling from "./components/Clubs/Manager/NewBilling";
import NewGame from "./components/Clubs/Manager/NewGame";
import Games from "./components/Clubs/Manager/Games";
import Competitions from "./components/Clubs/Manager/Competitions";
import EditCompetition from "./components/Clubs/Manager/EditCompetition";
import NewCompetition from "./components/Clubs/Manager/NewCompetition";
import NewGameTeam from "./components/Clubs/Manager/NewGameTeam";
import GameTeams from './components/Clubs/Manager/GameTeams'
import EditGameTeam from "./components/Clubs/Manager/EditGameTeam";
import Children from "./components/Clubs/Parent/Children";
import Billing from './components/Clubs/Parent/Billing'
import GamesParent from './components/Clubs/Parent/Games'
import BillingPay from "./components/Clubs/Parent/BillingPay";
import AppLayout from './components/AppLayout'
import EditGame from './components/Clubs/Manager/EditGame'
import GeneralCompanyInfo from './components/Company/GeneralCompanyInfo'
import CompanyTeams from './components/Company/CompanyTeams'
import MemberAbout from './components/Clubs/Member/MemberAbout'
import MemberBilling from './components/Clubs/Member/MemberBilling'
import MemberBillingPay from './components/Clubs/Member/MemberBillingPay'
import MemberAttendance from './components/Clubs/Member/MemberAttendance'
import MemberGames from './components/Clubs/Member/MemberGames'
import TrainingClass from './components/Clubs/Trainer/TrainingClass'
import TrainerGames from "./components/Clubs/Trainer/TrainerGames";
import TrainingsManager from './components/Clubs/Manager/TrainingsManager'
import CompanyDetail from "./components/Client/CompanyDetails";
import Contacts from './components/Client/Contacts'
import Landing from './components/Client/Landing'
import TrainerMembers from './components/Clubs/Trainer/TrainerMembers'
import ManagerShowGame from './components/Clubs/Manager/ManagerShowGame'
import TrainerShowGame from './components/Clubs/Trainer/TrainerShowGame'
import ParentShowGame from './components/Clubs/Parent/ParentShowGame'
import MemberShowGame from './components/Clubs/Member/MemberShowGame'
import GameParticipants from './components/Games/GameParticipants'
import GameBilling from './components/Games/GameBilling'
import GameTeamDetails from "./components/Games/GameTeamDetails";
import TrainerGameTeams from "./components/Clubs/Trainer/TrainerGameTeams";
import CompetitionDetails from "./components/Games/CompetitionDetails";
import CompetitionBillingManager from "./components/Clubs/Manager/CompetitionBillingManager";
import CompetitionBillingTrainer from "./components/Clubs/Trainer/CompetitionBillingTrainer";
import Login from "./components/Client/Login";
import CompetitionCompaniesManager from './components/Clubs/Manager/CompetitionCompaniesManager'
import CompetitionTeams from "./components/Games/CompetitionTeams";
import CompetitionGroups from './components/Games/CompetitionGroups'
import ClientNewCompany from "./components/Client/ClientNewCompany";
import newStore from "./storage/NewStore";
import EditAgreement from "./components/Agreement/EditAgreement";
import adminStore from "./storage/AdminStore";
import Agreements from './components/Agreement/Agreements'
import {AGREEMENT_CLUB1_OWNER, AGREEMENT_CLUB_MEMBER} from './models/Agreement'
import AgreementGuard from "./components/Agreement/AgreementGuard";
import SignAgreement from "./components/Agreement/SignAgreement";
import NewCompanyUserType from "./components/Clubs/Manager/Member/NewCompanyUserType";
import NewMember from "./components/Clubs/Manager/Member/NewMember";
import NewParent from "./components/Clubs/Manager/Member/NewParent";
import NewStaff from "./components/Clubs/Manager/Member/NewStaff";
import EditCompanyUser from "./components/Clubs/Manager/Member/EditCompanyUser";
import MemberBillings from "./components/Clubs/Manager/Member/MemberBillings";
import ClubsSearch from './components/Client/ClubsSearch'
import CompanyImports from "./components/Admin/CompanyImports";


const App = observer(() => {
    const {tg, isValid, onToggleTheme} = useTelegram();
    const [searchParams] = useSearchParams();
    const toast = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const isAgreementPage = location.pathname === "/agreement/sign";

    useLayoutEffect(() => {
      applyBranding()
    }, [])

    useEffect(() => {
        const jwt = searchParams.get("jwt");
        if (jwt) appStore.setJwt(jwt);
    }, [searchParams]);

    useEffect(() => {
    let cancelled = false;

    const init = async () => {
        await appStore.init({
            minimal: isAgreementPage
        });

        if (cancelled || isAgreementPage) {
            return;
        }

        if (appStore.user.companies.length === 1) {
            await clubStore.getClub(
                appStore.user.companies[0].company.id
            );
        }

        if (cancelled) {
            return;
        }

        if (appStore.user.id > 0) {
            newStore.checkPendingCompanies();

            newStore.company.setProperty(
                'owner_phone',
                appStore.user.phone_number
            );
        }
    };

    init();

    return () => {
        cancelled = true;
    };
}, [isAgreementPage]);

    useEffect(() => {
        tg?.ready();
        if (tg?.safeAreaInset) {
          document.documentElement.style.setProperty(
            '--tg-safe-top',
            `${tg.safeAreaInset.top}px`
          );
          document.documentElement.style.setProperty(
            '--tg-safe-bottom',
            `${tg.safeAreaInset.bottom}px`
          );
        }
        if (tg) {
          tg.onEvent('viewportChanged', () => {
            document.documentElement.style.setProperty(
              '--viewport-h',
              `${tg.viewportHeight}px`
            );
          });
        }

        if (isValid()) {
            tg.BackButton.show();
            tg.BackButton.onClick(() => {
                if (window.history.state.idx === 0) tg?.close();
                else window.history.back();
            });
            tg.onEvent("themeChanged", onToggleTheme);
            return () => tg.offEvent("themeChanged", onToggleTheme);
        }
    }, [tg]);

    // Map your menu from store
    const menuItems = (items) =>
        items.map((item) => {
            const hasChildren = Array.isArray(item.items) && item.items.length > 0;

            return {
                label: appStore.tr[item.label] || item.label,
                icon: item.icon,
                items: hasChildren ? menuItems(item.items) : undefined,
                role: item.role,
                url: item.url
            };
        });

    const items = menuItems(clubStore.getMenu || []);

    return (
        <PrimeReactProvider>
            <Loading/>
            <Toast ref={toast}/>
                <AgreementGuard />
                <Routes>
                  <Route path="demo" element={<Landing/>}/>
                  <Route path="demo/:lang" element={<Landing/>}/>
                    <Route path="/agreement/sign" element={<SignAgreement />}/>
                  <Route path="*" element={
                    <AppLayout items={items}>
                      <Routes>
                      <Route index element={<Client/>}/>
                      <Route path="/companies" element={<Companies/>}/>
                      <Route path="/clubs/search" element={<ClubsSearch/>}/>
                      <Route path="/settings" element={<Settings/>}/>
                      <Route path="/club/:id" element={<CompanyDetail/>}/>
    
                      <Route path="/contacts" element={<Contacts/>}/>
                      <Route path="/login" element={<Login/>}/>
    
                      {clubStore.club?.id > 0 && (
                        <>
                          <Route path="/company/info" element={<GeneralCompanyInfo/>}/>
                          <Route path="/company/teams" element={<CompanyTeams/>}/>
                        </>
                      )}
                      {appStore.user?.is_admin && (
                        <>
                          <Route path="/admin" element={<AdminHome/>}/>
                          <Route path="/admin/companies" element={<AdminCompanies/>}/>
                          <Route path="/admin/new_company" element={<NewCompany/>}/>
                          <Route path="/admin/edit_company/:id" element={<EditCompany/>}/>
                          <Route path="/admin/edit_agreement/:id" element={<EditAgreement agreement={adminStore.agreement} basePath={'/admin'}/>}/>
                          <Route path="/admin/agreements" element={<Agreements type={AGREEMENT_CLUB1_OWNER} basePath={'/admin'}/>}/>
                          <Route path="/admin/company_imports" element={<CompanyImports/>}/>
                        </>
                      )}
    
                      {clubStore.club?.is_manager && (
                        <>
                          <Route path="/manager/company/info" element={<ShowCompany/>}/>
                          <Route path="/manager/company/edit" element={<ManagerEditCompany/>}/>
                          <Route path="/manager/member/list" element={<Members/>}/>
                          <Route path="/manager/member/new" element={<NewCompanyUserType/>}/>
                          <Route path="/manager/member/new/member" element={<NewMember/>}/>
                          <Route path="/manager/member/new/parent" element={<NewParent/>}/>
                          <Route path="/manager/member/new/staff/:roleId" element={<NewStaff/>}/>
                          <Route path="/manager/member/edit/:id" element={<EditCompanyUser/>}/>
                          <Route path="/manager/member/:id/billing" element={<MemberBillings/>}/>
                          <Route path="/manager/branch/new" element={<NewBranch/>}/>
                          <Route path="/manager/branch/list" element={<Branches/>}/>
                          <Route path="/manager/branch/edit/:id" element={<EditBranch/>}/>
                          <Route path="/manager/team/new" element={<NewTeam/>}/>
                          <Route path="/manager/team/list" element={<ManagerTeams/>}/>
                          <Route path="/manager/team/edit/:id" element={<EditTeam/>}/>
                          <Route path="/manager/team/show/:id" element={<ShowTeam/>}/>
                          <Route path="/manager/trainings/list" element={<TrainingsManager/>}/>
                          <Route path="/manager/trainings/attendance" element={<AttendanceManager/>}/>
                          <Route path="/manager/trainings/attendance/detail/:id" element={<AttendanceDetail/>}/>
                          <Route path="/manager/billing/info" element={<BillingInfo/>}/>
                          <Route path="/manager/billing/group/:id" element={<BillingGroupDetail/>}/>
                          <Route path="/manager/billing/detail/:id" element={<BillingDetail/>}/>
                          <Route path="/manager/billing/create" element={<NewBilling/>}/>
                          <Route path="/manager/games/list" element={<Games/>}/>
                          <Route path="/manager/games/create" element={<NewGame/>}/>
                          <Route path="/manager/games/edit/:id" element={<EditGame/>}/>
                          <Route path="/manager/games/participants/:id" element={<GameParticipants/>}/>
                          <Route path="/manager/games/show/:id" element={<ManagerShowGame/>}/>
                          <Route path="/manager/games/billing/:id" element={<GameBilling/>}/>
                          <Route path="/manager/games/game_team/new" element={<NewGameTeam/>}/>
                          <Route path="/manager/games/game_team/:id" element={<EditGameTeam/>}/>
                          <Route path="/manager/games/game_team_details/:id" element={<GameTeamDetails/>}/>
                          <Route path="/manager/tournaments/list" element={<Competitions/>}/>
                          <Route path="/manager/tournament/edit/:id" element={<EditCompetition/>}/>
                          <Route path="/manager/tournament/new" element={<NewCompetition/>}/>
                          <Route path="/manager/tournament/team/create" element={<NewGameTeam/>}/>
                          <Route path="/manager/tournament/team/edit/:id" element={<EditGameTeam/>}/>
                          <Route path="/manager/tournaments/show/:id" element={<CompetitionDetails role={"MGR"}/>}/>
                          <Route path="/manager/tournament/billing/:id" element={<CompetitionBillingManager/>}/>
                          <Route path="/manager/tournament/companies/:id" element={<CompetitionCompaniesManager/>}/>
                          <Route path="/manager/tournament/teams/:competition_id"
                                 element={<CompetitionTeams role={"MGR"}/>}/>
                          <Route path="/manager/tournament/groups/:id" element={<CompetitionGroups role={"MGR"}/>}/>
                          <Route path="/manager/game_teams/list" element={<GameTeams/>}/>
                            <Route path="/manager/edit_agreement/:id" element={<EditAgreement agreement={clubStore.agreement} basePath={'/manager'}/>}/>
                            <Route path="/manager/agreements" element={<Agreements type={AGREEMENT_CLUB_MEMBER} basePath={'/manager'}/>}/>
                        </>
                      )}
    
                      {clubStore.club?.is_trainer && (
                        <>
                          <Route path="/trainer/team/list" element={<TrainerTeams/>}/>
                          <Route path="/trainer/trainings" element={<Trainings/>}/>
                          <Route path="/trainer/members" element={<TrainerMembers/>}/>
                          <Route path="/trainer/class/:team_id/:date" element={<TrainingClass/>}/>
                          <Route path="/trainer/games/list" element={<TrainerGames/>}/>
                          <Route path="/trainer/game/:id" element={<TrainerShowGame/>}/>
                          <Route path="/trainer/game_teams/list" element={<TrainerGameTeams/>}/>
                          <Route path="/trainer/games/game_team/new" element={<NewGameTeam/>}/>
                          <Route path="/trainer/games/game_team/:id" element={<EditGameTeam/>}/>
                          <Route path="/trainer/games/game_team_details/:id" element={<GameTeamDetails/>}/>
                          <Route path="/trainer/tournament/billing/:id" element={<CompetitionBillingTrainer/>}/>
                        </>
                      )}
                      {clubStore.club?.is_parent && (
                        <>
                          <Route path="/parent/children/list" element={<Children/>}/>
                          <Route path="/parent/children/billing" element={<Billing/>}/>
                          <Route path="/parent/children/pay/:id" element={<BillingPay/>}/>
                          <Route path="/parent/children/attendance" element={<Attendance/>}/>
                          <Route path="/parent/children/games" element={<GamesParent/>}/>
                          <Route path="/parent/game/:id" element={<ParentShowGame/>}/>
                        </>
                      )}
    
                      {clubStore.club?.is_member && (
                        <>
                          <Route path="/member/about" element={<MemberAbout/>}/>
                          <Route path="/member/billing" element={<MemberBilling/>}/>
                          <Route path="/member/billing/pay/:id" element={<MemberBillingPay/>}/>
                          <Route path="/member/attendance" element={<MemberAttendance/>}/>
                          <Route path="/member/games" element={<MemberGames/>}/>
                          <Route path="/member/game/:id" element={<MemberShowGame/>}/>
                        </>
                      )}
                      <Route path={'/add'} element={<ClientNewCompany/>}/>
                      <Route path="*" element={<NotFound/>}/>
                        </Routes>
                    </AppLayout>
                  }/>
                </Routes>
            <Notification/>
        </PrimeReactProvider>
    );
});

export default App;
